import React, { useState, useEffect } from 'react';
import { Layout, PageHeader } from '../components/Layout';
import { useAuth } from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  DoorOpen, 
  Users, 
  CalendarCheck, 
  TrendingUp, 
  Euro,
  AlertCircle,
  Clock,
  ArrowRight,
  FileText,
  AlertTriangle
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const StatCard = ({ icon: Icon, title, value, subtitle, color = 'blue', onClick }) => {
  const colorClasses = {
    blue: 'bg-[#1e3a8a] text-white',
    gold: 'bg-[#c5a059] text-white',
    emerald: 'bg-emerald-500 text-white',
    amber: 'bg-amber-500 text-white',
    red: 'bg-red-500 text-white',
  };

  return (
    <Card 
      className={`card-hover border-slate-200 ${onClick ? 'cursor-pointer' : ''}`} 
      onClick={onClick}
      data-testid={`stat-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">{title}</p>
            <p className="text-3xl font-bold text-[#0f172a] mt-1">{value}</p>
            {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon size={24} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AlertItem = ({ alert }) => {
  const typeClasses = {
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    error: 'bg-red-50 border-red-200 text-red-800',
  };

  return (
    <div className={`flex items-center gap-3 p-3 rounded-md border ${typeClasses[alert.type] || typeClasses.info}`}>
      <AlertCircle size={18} />
      <span className="text-sm">{alert.message}</span>
    </div>
  );
};

const ReservationItem = ({ reservation, onClick }) => {
  const statusColors = {
    brouillon: 'bg-slate-100 text-slate-700',
    en_attente: 'bg-amber-100 text-amber-800',
    confirme: 'bg-emerald-100 text-emerald-800',
    annule: 'bg-red-100 text-red-800',
    termine: 'bg-blue-100 text-blue-800',
  };

  const statusLabels = {
    brouillon: 'Brouillon',
    en_attente: 'En attente',
    confirme: 'Confirmé',
    annule: 'Annulé',
    termine: 'Terminé',
  };

  return (
    <div 
      className="flex items-center justify-between p-4 hover:bg-slate-50 rounded-lg cursor-pointer transition-colors"
      onClick={onClick}
      data-testid={`upcoming-reservation-${reservation.id}`}
    >
      <div className="flex items-center gap-4">
        <div className="h-12 w-12 rounded-lg bg-[#1e3a8a]/10 flex items-center justify-center">
          <Clock className="text-[#1e3a8a]" size={20} />
        </div>
        <div>
          <p className="font-medium text-slate-900">{reservation.title}</p>
          <p className="text-sm text-slate-500">
            {reservation.room_name}
          </p>
          <p className="text-sm text-slate-500">
            {format(parseISO(reservation.start_datetime), "d MMM yyyy 'à' HH:mm", { locale: fr })}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Badge className={statusColors[reservation.status]}>
          {statusLabels[reservation.status]}
        </Badge>
        <ArrowRight size={18} className="text-slate-400" />
      </div>
    </div>
  );
};

// User Dashboard Component
function UserDashboard({ user }) {
  const [data, setData] = useState({
    upcomingReservations: [],
    pendingInvoices: [],
    loading: true
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const [reservationsRes, invoicesRes] = await Promise.all([
        api.get('/dashboard/upcoming-reservations?limit=10'),
        api.get('/invoices')
      ]);
      
      // Filter pending invoices
      const pendingInvoices = invoicesRes.data.filter(inv => 
        inv.status === 'en_attente' || inv.status === 'partiel'
      );
      
      setData({
        upcomingReservations: reservationsRes.data,
        pendingInvoices,
        loading: false
      });
    } catch (error) {
      toast.error('Erreur lors du chargement du tableau de bord');
      setData(prev => ({ ...prev, loading: false }));
    }
  };

  if (data.loading) {
    return (
      <Layout>
        <PageHeader title="Mon espace" />
        <div className="p-8 flex items-center justify-center">
          <div className="spinner" style={{ width: 40, height: 40 }} />
        </div>
      </Layout>
    );
  }

  const totalPending = data.pendingInvoices.reduce((sum, inv) => 
    sum + (inv.amount - (inv.amount_paid || 0)), 0
  );

  // Check for overdue invoices
  const now = new Date();
  const overdueInvoices = data.pendingInvoices.filter(inv => 
    new Date(inv.due_date) < now
  );

  return (
    <Layout>
      <PageHeader 
        title="Mon espace" 
        description={`Bienvenue, ${user.first_name}`}
      />
      
      <div className="p-8 space-y-8" data-testid="user-dashboard">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <StatCard 
            icon={CalendarCheck} 
            title="Réservations à venir" 
            value={data.upcomingReservations.length}
            color="blue"
            onClick={() => navigate('/reservations')}
          />
          <StatCard 
            icon={Euro} 
            title="Règlements en attente" 
            value={`${totalPending.toLocaleString('fr-FR')} €`}
            color={totalPending > 0 ? 'amber' : 'emerald'}
            onClick={() => navigate('/billing')}
          />
          <StatCard 
            icon={AlertTriangle} 
            title="Retards de règlement" 
            value={overdueInvoices.length}
            color={overdueInvoices.length > 0 ? 'red' : 'emerald'}
            onClick={() => navigate('/billing')}
          />
        </div>

        {/* Alerts */}
        {overdueInvoices.length > 0 && (
          <Card className="border-red-200 bg-red-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-red-800 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Alertes de règlement
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {overdueInvoices.map((inv) => (
                  <div key={inv.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-red-200">
                    <div>
                      <p className="font-medium text-red-800">Appel {inv.invoice_number}</p>
                      <p className="text-sm text-red-600">
                        Échu le {format(parseISO(inv.due_date), 'd MMM yyyy', { locale: fr })}
                      </p>
                    </div>
                    <p className="font-bold text-red-800">
                      {(inv.amount - (inv.amount_paid || 0)).toLocaleString('fr-FR')} €
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upcoming Reservations */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#1e3a8a]" style={{ fontFamily: 'Merriweather, serif' }}>
                Mes prochaines réservations
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.upcomingReservations.length > 0 ? (
                <div className="space-y-2">
                  {data.upcomingReservations.map((res) => (
                    <ReservationItem 
                      key={res.id} 
                      reservation={res}
                      onClick={() => navigate('/reservations')}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <CalendarCheck className="mx-auto mb-2" size={32} />
                  <p>Aucune réservation à venir</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pending Payments */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#1e3a8a]" style={{ fontFamily: 'Merriweather, serif' }}>
                Appels en attente de règlement
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.pendingInvoices.length > 0 ? (
                <div className="space-y-2">
                  {data.pendingInvoices.map((inv) => (
                    <div 
                      key={inv.id}
                      className="flex items-center justify-between p-4 hover:bg-slate-50 rounded-lg cursor-pointer transition-colors"
                      onClick={() => navigate('/billing')}
                    >
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-lg bg-amber-100 flex items-center justify-center">
                          <FileText className="text-amber-600" size={20} />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{inv.invoice_number}</p>
                          <p className="text-sm text-slate-500">
                            Échéance: {format(parseISO(inv.due_date), 'd MMM yyyy', { locale: fr })}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-slate-900">
                          {(inv.amount - (inv.amount_paid || 0)).toLocaleString('fr-FR')} €
                        </p>
                        <Badge className="bg-amber-100 text-amber-800">
                          {inv.status === 'partiel' ? 'Partiel' : 'En attente'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Euro className="mx-auto mb-2 text-emerald-500" size={32} />
                  <p>Aucune facture en attente</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}

// Admin/Gestionnaire Dashboard Component
function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, upcomingRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/dashboard/upcoming-reservations?limit=5')
      ]);
      setStats(statsRes.data);
      setUpcoming(upcomingRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <PageHeader title="Tableau de bord" />
        <div className="p-8 flex items-center justify-center">
          <div className="spinner" style={{ width: 40, height: 40 }} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHeader 
        title="Tableau de bord" 
        description="Vue d'ensemble de l'activité"
      />
      
      <div className="p-8 space-y-8" data-testid="dashboard-content">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard 
            icon={DoorOpen} 
            title="Salles" 
            value={stats?.total_rooms || 0}
            color="blue"
          />
          <StatCard 
            icon={Users} 
            title="Bénéficiaires" 
            value={stats?.total_clients || 0}
            color="gold"
          />
          <StatCard 
            icon={CalendarCheck} 
            title="Réservations à venir" 
            value={stats?.upcoming_reservations || 0}
            color="emerald"
          />
          <StatCard 
            icon={TrendingUp} 
            title="Taux d'occupation" 
            value={`${stats?.occupancy_rate || 0}%`}
            subtitle="Ce mois"
            color="amber"
          />
        </div>

        {/* Financial Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="border-slate-200" data-testid="monthly-revenue-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-[#1e3a8a]" style={{ fontFamily: 'Merriweather, serif' }}>
                Contributions du mois
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-baseline gap-2">
                <Euro className="text-[#c5a059]" size={24} />
                <span className="text-3xl font-bold text-[#0f172a]">
                  {(stats?.monthly_revenue || 0).toLocaleString('fr-FR')} €
                </span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200" data-testid="pending-payments-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-[#1e3a8a]" style={{ fontFamily: 'Merriweather, serif' }}>
                Règlements en attente
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-baseline gap-2">
                <Euro className="text-amber-500" size={24} />
                <span className="text-3xl font-bold text-[#0f172a]">
                  {(stats?.pending_payments || 0).toLocaleString('fr-FR')} €
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Alerts */}
          <Card className="border-slate-200" data-testid="alerts-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#1e3a8a]" style={{ fontFamily: 'Merriweather, serif' }}>
                Alertes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.alerts && stats.alerts.length > 0 ? (
                <div className="space-y-3">
                  {stats.alerts.map((alert, index) => (
                    <AlertItem key={`alert-${alert.type || index}-${alert.message?.substring(0,10) || index}`} alert={alert} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <AlertCircle className="mx-auto mb-2 text-emerald-500" size={32} />
                  <p>Aucune alerte</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Reservations */}
          <Card className="border-slate-200" data-testid="upcoming-reservations-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#1e3a8a]" style={{ fontFamily: 'Merriweather, serif' }}>
                Prochaines réservations
              </CardTitle>
            </CardHeader>
            <CardContent>
              {upcoming.length > 0 ? (
                <div className="space-y-2">
                  {upcoming.map((res) => (
                    <ReservationItem 
                      key={res.id} 
                      reservation={res}
                      onClick={() => navigate(`/reservations`)}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <CalendarCheck className="mx-auto mb-2" size={32} />
                  <p>Aucune réservation à venir</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  
  if (user?.role === 'utilisateur') {
    return <UserDashboard user={user} />;
  }
  
  return <AdminDashboard />;
}
